import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, catchError, debounceTime, from, map, Observable, of, Subject, switchMap, tap } from 'rxjs';

// Services
import { IntegrationService } from '../../integration/services/integration.service';
import { ViewStateService } from '../../../../core/_services/view-state/view-state.service';
import { TokenService } from '../../../../core/_services/token/token.service';

// Models
import { C2PAManifest } from '../models/media-list-item-manifest.model';
import { IntegrationProvider } from '../../../models/integration-providers.model';
import { MediaItem, MediaListItem, MediaCollection, CollectionResponse } from '../models/media-list.model';
import { MediaTypeFilter } from '../enums/media-type-filter.enum';

// Shared
import { AssetItem as AssetListItem, AssetsByAssetId } from '@oddr/user-schemas';
import { GetAssetDownloadUrlResponse } from '@oddr/user-services';

// Directives
import { SortColumn, SortDirection } from '../directives/media-list-sort.directive';

interface SearchResult {
  mediaListItems: MediaListItem[];
  total: number;
}

interface State {
  filter: MediaTypeFilter;
  page: number;
  pageSize: number;
  refreshList: boolean;
  searchTerm: string;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
}

const compare = (v1: string | Date | null, v2: string | Date | null) => {
  if (v1 == null && v2 == null) return 0;
  if (v1 == null) return -1;
  if (v2 == null) return 1;
  return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
};

function sort(mediaList: MediaListItem[], column: SortColumn, direction: string): MediaListItem[] {
  if (direction === '' || (column !== 'status' && column !== 'dateUploaded')) return mediaList;
  else return [...mediaList].sort((a, b) => {
    const res = compare(a[column], b[column]);
    return direction === 'asc' ? res : -res;
  });
}

// function matches(mediaListItem: MediaListItem, term: string) {
//  return (
//     mediaListItem.title?.toLowerCase().includes(term.toLowerCase()) ||
//     mediaListItem.description?.toLowerCase().includes(term.toLowerCase())
//   );
// }

/**
 * Pulled from example for ngBootstrap Table.
 * https://ng-bootstrap.github.io/#/components/table/examples#complete
 */
@Injectable({
  providedIn: 'root',
})
export class MediaAssetService {

  // Services
  private readonly httpBackendHandler = inject(HttpBackend);
  private readonly httpClient = inject(HttpClient);
  private readonly integrationService = inject(IntegrationService);
  private readonly tokenService = inject(TokenService);
  private readonly viewStateService = inject(ViewStateService);

  // Team context
  private userSub: string | null = null;

  // Subjects
  private mediaListLoading = new BehaviorSubject<boolean>(true);
  private viewableMediaList = new BehaviorSubject<MediaListItem[] | undefined>(undefined);
  private viewableMediaListNumberOfDisplayedItems = new BehaviorSubject<number>(0);
  private viewableMediaListSearch = new Subject<void>();

  // Viewing state
  private mediaList: MediaListItem[] = [];
  private mediaListState: State = {
    filter: MediaTypeFilter.All,
    page: 1,
    pageSize: 20,
    refreshList: false,
    searchTerm: '',
    sortColumn: 'dateUploaded',
    sortDirection: 'desc',
  };

  constructor() {
    this.viewableMediaListSearch
      .pipe(
        tap(() => {
          if (this.mediaListState.refreshList) this.mediaListLoading.next(true);
        }),
        debounceTime(200),
        switchMap(() => this.search()),
        tap(() => this.mediaListLoading.next(false)),
      )
      .subscribe((response: SearchResult) => {
        this.viewableMediaList.next(response.mediaListItems);
        this.viewableMediaListNumberOfDisplayedItems.next(response.total);
      });
  }

  /**
   * The loading indicator to advise if the list of media for the authenticated user is being retrieved from the data source.
   * @type {Observable<boolean>}
   * @memberof MediaAssetService
   */
  public mediaListLoading$: Observable<boolean> = this.mediaListLoading.asObservable();

  /**
   * The list of media for the authenticated user.
   * @type {Observable<MediaListItem[]>}
   * @memberof MediaAssetService
   */
  public viewableMediaList$: Observable<MediaListItem[] | undefined> = this.viewableMediaList.asObservable();

  /**
   * The number of media items currently being displayed in the list after filtering and sorting.
   * @type {Observable<number>}
   * @memberof MediaAssetService
   */
  public viewableMediaListNumberOfDisplayedItems$: Observable<number> = this.viewableMediaListNumberOfDisplayedItems.asObservable();

  /**
   * The media list type being viewed.
   * @type {MediaTypeFilter}
   * @memberof MediaAssetService
   */
  get mediaListFilter(): MediaTypeFilter {
    return this.mediaListState.filter;
  }

  set mediaListFilter(filter: MediaTypeFilter) {
    this.setState({ filter });
  }

  /**
   * The media list page being viewed.
   * @type {number}
   * @memberof MediaAssetService
   */
  get mediaListPage(): number {
    return this.mediaListState.page;
  }

  set mediaListPage(page: number) {
    this.setState({ page });
  }

  /**
   * The media list page size currently selected.
   * @type {number}
   * @memberof MediaAssetService
   */
  get mediaListPageSize(): number {
    return this.mediaListState.pageSize;
  }

  set mediaListPageSize(pageSize: number) {
    this.setState({ pageSize });
  }

  /**
   * The media list search term.
   * @type {string}
   * @memberof MediaAssetService
   */
  get mediaListSearchTerm(): string {
    return this.mediaListState.searchTerm;
  }

  set mediaListSearchTerm(searchTerm: string) {
    this.setState({ searchTerm });
  }

  /**
   * The media list column being sorted.
   * @type {SortColumn}
   * @memberof MediaAssetService
   */
  get mediaListSortColumn(): SortColumn {
    return this.mediaListState.sortColumn;
  }

  set mediaListSortColumn(sortColumn: SortColumn) {
    this.setState({ sortColumn });
  }

  /**
   * The media list column sort direction.
   * @type {SortDirection}
   * @memberof MediaAssetService
   */
  get mediaListSortDirection(): SortDirection {
    return this.mediaListState.sortDirection;
  }

  set mediaListSortDirection(sortDirection: SortDirection) {
    this.setState({ sortDirection });
  }

  /**
   * Sets the media list back to its original state of `undefined`.
   * @memberof MediaAssetService
   */
  clearMediaList(): void {
    this.viewableMediaList.next(undefined);
  }

  /**
   * Retrieves the URL location of the asset's C2PA signed copy and downloads it for the user.
   * @param assetId the identifier of the asset.
   * @memberof MediaAssetService
   */
  downloadC2paSignedCopy(assetId: string): Observable<void> {
    // Team assets are keyed by the team id, so a team upload needs the team
    // endpoint; the personal one can never find it.
    return from(this.resolveAssetsEndpoint())
      .pipe(
        switchMap(endpoint => this.httpClient.get<GetAssetDownloadUrlResponse>(`${endpoint}/${assetId}/download`)),
      )
      .pipe(
        switchMap((response: GetAssetDownloadUrlResponse) => {
          window.open(response.signedAssetDownloadUrl);
          return of();
        }),
      );
  }

  /**
   * Retrieves a list of all uploaded media for the authenticated user.
   * @returns a list of media items.
   * @memberof MediaAssetService
   */
  getMediaList(): Observable<MediaListItem[]> {
    // Ensure the media list is set to refresh
    Object.assign(this.mediaListState, { refreshList: true });

    return from(this.resolveAssetsEndpoint()).pipe(
      switchMap(endpoint => this.httpClient.get<AssetListItem[]>(endpoint)),
    )
      .pipe(
        map((response: AssetListItem[]) => {
          const mediaListItems = [] as MediaListItem[];
          const collectionMap = new Map<string, MediaListItem[]>();

          for (const item of response) {
            const integrations = (item.integrations ?? []).map(integration => this.mapProviderResponse(integration));

            const listItem: MediaListItem = {
              assetId: item.assetId,
              datePublished: item.datePublished ? new Date(item.datePublished) : null,
              dateUploaded: item.dateUploaded ? new Date(item.dateUploaded) : null,
              integrations,
              oddrLink: item.oddrLink,
              status: item.status,
              thumbnail: item.thumbnail,
              title: item.title,
              type: item.type,
              groupId: item.groupId,
              groupIndex: item.groupIndex,
            } as MediaListItem;

            if (listItem.groupId) {
              const members = collectionMap.get(listItem.groupId) ?? [];
              members.push(listItem);
              collectionMap.set(listItem.groupId, members);
            } else {
              mediaListItems.push(listItem);
            }
          }

          // Collapse each collection group into a single representative row
          for (const [groupId, members] of collectionMap) {
            members.sort((a, b) => (a.groupIndex ?? 0) - (b.groupIndex ?? 0));
            const first = members[0];
            mediaListItems.push({
              assetId: groupId,
              datePublished: first.datePublished,
              dateUploaded: first.dateUploaded,
              integrations: first.integrations,
              oddrLink: first.oddrLink,
              status: first.status,
              thumbnail: first.thumbnail,
              title: first.title,
              type: first.type,
              groupId,
              isCollection: true,
              collectionMembers: members,
            } as MediaListItem);
          }

          this.mediaList = mediaListItems;
          this.viewableMediaListSearch.next();
          return mediaListItems;
        }),
        catchError(() => {
          this.viewableMediaList.next([]);
          this.viewableMediaListNumberOfDisplayedItems.next(-1);
          return [];
        }),
      );
  }

  /**
   * Retrieves the uploaded media via its asset identifier.
   * @param assetId the identifier of the media asset.
   * @returns the media item.
   * @memberof MediaAssetService
   */
  getMediaAsset(assetId: string): Observable<MediaItem> {
    return this.httpClient.get<any>(`ppf/assets/${assetId}`)
      .pipe(
        switchMap((response: AssetsByAssetId) => {
          const mediaAssetItem = new MediaItem(response);

          // If manifest was returned from DB, use it directly
          if (mediaAssetItem.manifest) {
            return of(mediaAssetItem);
          }

          // Fallback: fetch from S3 manifest URI for older assets without DB manifest
          if (response.manifestUri) {
            const httpClientWithBackendHandler = new HttpClient(this.httpBackendHandler);
            const url = new URL(response.manifestUri);
            return httpClientWithBackendHandler
              .get<any>(url.toString(), { headers: new HttpHeaders({ 'ngsw-bypass': '' }) })
              .pipe(
                map((manifest: any) => {
                  mediaAssetItem.manifest = new C2PAManifest(manifest);
                  return mediaAssetItem;
                }),
                catchError(() => of(mediaAssetItem)),
              );
          }

          return of(mediaAssetItem);
        }),
      );
  }

  /**
   * Fetches a collection (multi-image post) and its member assets for the public
   * /c/:groupId page. Members arrive as full public assets and are mapped to
   * MediaItem for consistent rendering with the single media page.
   */
  getMediaCollection(groupId: string): Observable<MediaCollection> {
    return this.httpClient
      .get<CollectionResponse>(`ppf/assets/collection/${groupId}`)
      .pipe(map((response: CollectionResponse) => new MediaCollection(response)));
  }

  /**
   * Maps an incoming integration response to a display-ready provider object.
   *
   * This is provider-agnostic: it passes through every field returned by the
   * backend (including provider-specific fields like pageId, visibility,
   * madeForKids and any error/uploadStatus details) and overlays the resolved
   * logo image set. Adding a new integration provider requires no change here —
   * only registering its logo in IntegrationService.resolveImageSet().
   * @private
   * @param integration the raw integration response from the backend.
   * @returns a display-ready integration object with logo image data.
   * @memberof MediaAssetService
   */
  private mapProviderResponse(integration: any): any {
    const resolvedImageSet = this.integrationService.resolveImageSet(integration.provider as IntegrationProvider);
    return {
      ...integration,
      ...(integration.scheduledTime ? { scheduledTime: new Date(integration.scheduledTime) } : {}),
      height: resolvedImageSet.icon.height,
      path: resolvedImageSet.icon.path,
      width: resolvedImageSet.icon.width,
    };
  }

  private async resolveAssetsEndpoint(): Promise<string> {
    const vs = this.viewStateService.viewState();
    const teamId = vs?.teamId ?? null;

    if (teamId) {
      if (!this.userSub) {
        this.userSub = await this.tokenService.sub();
      }
      if (teamId !== this.userSub) {
        return `tms/assets/${teamId}`;
      }
    }
    return 'usr/assets';
  }

  /**
   * Performs list operations to display the list as the state requires.
   * @private
   * @returns a filtered, sorted list of items.
   * @memberof MediaAssetService
   */
  private search(): Observable<SearchResult> {
    const { filter, page, pageSize, sortColumn, sortDirection } = this.mediaListState;

    // 1. Sort
    let mediaListItems = sort(this.mediaList, sortColumn, sortDirection);

    // 2. Filter
    if (filter !== MediaTypeFilter.All) mediaListItems = mediaListItems.filter((item: MediaListItem) => item.type === filter);
    const total = mediaListItems.length;

    // 3. Paginate
    mediaListItems = mediaListItems.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
    return of({ mediaListItems, total });
  }

  /**
   * Sets the state based on the partial object and value provided.
   * @private
   * @param patchValue the property and new value to update.
   * @memberof MediaAssetService
   */
  private setState(patchValue: Partial<State>): void {
    Object.assign(this.mediaListState, patchValue);
    Object.assign(this.mediaListState, { refreshList: false });
    this.viewableMediaListSearch.next();
  }
}
